// SVG/PNG/XLSX export for FlowchartBuilder, extracted out of the component
// body. Read-only consumer of canvas + Gantt state — no new export logic,
// calls straight through to lib/svgExport.ts and lib/xlsxExport.ts exactly as
// before. Must be wired up AFTER useGanttSchedule() in FlowchartBuilder.tsx,
// since exportXLSX needs getStartDate/getEndDate from that hook's output.
import { buildExportSVGString } from './lib/svgExport';
import { buildExpandedGanttRows } from './lib/gantt';
import { buildTaskExportRows, exportTasksToXlsx } from './lib/xlsxExport';
import type { Box, Arrow, Department, Area } from './types/index';
import type { useGanttSchedule } from './useGanttSchedule';

type GanttScheduleOutput = ReturnType<typeof useGanttSchedule>;

interface UseCanvasExportParams {
  boxes: Box[];
  arrows: Arrow[];
  departments: Department[];
  areas: Area[];
  title: string;
  showLegend: boolean;
  showNotes: boolean;
  showStartDates: boolean;
  showFinishDates: boolean;
  showCriticalPathCanvas: boolean;
  showSubitemBadge: boolean;
  showUidBadge: boolean;
  showAreas: boolean;
  /** Subitem count per parent box ID (see FlowchartBuilder.tsx's subitemCountByBoxId) — needed
   *  here since `boxes` is already filtered to top-level-only by the time it reaches this hook. */
  subitemCountByBoxId: Map<number, number>;
  /** Full (not top-level-filtered) boxes/arrows — unlike `boxes`/`arrows` above, the XLSX
   *  task export DOES include subitems as their own rows (unlike the SVG/PNG canvas export),
   *  so it needs the raw lists to find them and their own dependency arrows. */
  allBoxes: Box[];
  allArrows: Arrow[];
  subitemGanttTasks: GanttScheduleOutput['subitemGanttTasks'];
  criticalPath: GanttScheduleOutput['criticalPath'];
  schedulingConflicts: GanttScheduleOutput['schedulingConflicts'];
  getStart: GanttScheduleOutput['getStart'];
  getEnd: GanttScheduleOutput['getEnd'];
  getStartDate: GanttScheduleOutput['getStartDate'];
  getEndDate: GanttScheduleOutput['getEndDate'];
}

export function useCanvasExport(params: UseCanvasExportParams) {
  const {
    boxes, arrows, departments, areas, title, showLegend, showNotes,
    showStartDates, showFinishDates, showCriticalPathCanvas,
    showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
    allBoxes, allArrows, subitemGanttTasks,
    criticalPath, schedulingConflicts, getStart, getEnd, getStartDate, getEndDate,
  } = params;

  const exportXLSX = (): void => {
    // Interleave each top-level task's subitems directly after it (same grouping
    // rule as the live Gantt/SVG export use for subitem rows, see buildExpandedGanttRows's
    // own doc comment) — reused here so a subitem gets its own export row, right under
    // its parent, instead of being invisible to this export the way the canvas itself
    // deliberately keeps it. Ordered by the subitem's own schedule start, not array order.
    const subitemStartById = new Map(subitemGanttTasks.map(t => [t.id, t.start]));
    const subitemsByParent = new Map<number, Box[]>();
    allBoxes.forEach(b => {
      if (b.parentBoxId === undefined) return;
      const list = subitemsByParent.get(b.parentBoxId) ?? [];
      list.push(b);
      subitemsByParent.set(b.parentBoxId, list);
    });
    subitemsByParent.forEach(list =>
      list.sort((a, b) => (subitemStartById.get(a.id) ?? 0) - (subitemStartById.get(b.id) ?? 0)),
    );
    const expandedBoxes = buildExpandedGanttRows<Box>({
      topLevelTasks: boxes,
      areaByBoxId: new Map(),
      getSubitemsFor: (parent) => subitemsByParent.get(parent.id) ?? [],
      makeAreaHeaderTask: () => null,
      includeSubitems: true,
      includeAreaGroups: false,
      includeRegularBoxes: true,
    });
    // Full (not top-level-only) arrows, so subitem-internal dependency arrows resolve too —
    // this doesn't change which predecessors a top-level box reports, since those are still
    // filtered by `toBox === b.id` per row either way.
    const rows = buildTaskExportRows(expandedBoxes, allArrows, getStartDate, getEndDate);
    exportTasksToXlsx(rows, title || 'flowchart');
  };

  const exportAs = (format: 'svg' | 'png' | 'xlsx') => {
    if (format === 'xlsx') {
      exportXLSX();
      return;
    }
    const endLabels = new Map<number, string>(boxes.map(b => [b.id, getEnd(b.id)]));
    const startLabels = new Map<number, string>(boxes.map(b => [b.id, getStart(b.id)]));
    const result = buildExportSVGString({
      boxes, arrows, departments, areas,
      showLegend, showNotes,
      showCriticalPath: showCriticalPathCanvas,
      showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
      criticalPath, schedulingConflicts,
      endLabels, startLabels, showStartDates, showFinishDates,
    });
    if (!result) return;
    const { svg, W, H } = result;
    const filename = title || 'flowchart';
    if (format === 'svg') {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${filename}.svg`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = W * scale; canvas.height = H * scale;
        const ctx = canvas.getContext('2d')!;
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, W, H);
        URL.revokeObjectURL(url);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${filename}.png`; a.click();
      };
      img.src = url;
    }
  };

  return { exportAs, exportXLSX };
}
